import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { motion, useScroll, useTransform } from "framer-motion";
import service from "../../static/images/services/service.png";
import main2 from "../../static/images/landing_page/main_2.png";
import about_us from "../../static/images/landing_page/about_us.jpg";

const AboutUs = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const values = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Excellence',
      description: 'We strive for excellence in every survey and consultation we provide, ensuring the highest quality standards.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: 'Integrity',
      description: 'We conduct all our business with complete honesty and transparency, building trust with every client.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Innovation',
      description: 'We embrace modern technology and innovative approaches to deliver superior maritime consultancy services.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Client Focus',
      description: 'Our clients are at the heart of everything we do. We tailor our services to meet their specific needs.'
    }
  ];

  const team = [
    {
      role: 'Expert Surveyors',
      description: 'Our certified surveyors bring decades of combined experience in maritime inspection and consultancy.',
      count: '10+'
    },
    {
      role: 'Marine Consultants',
      description: 'Specialized consultants with deep knowledge of shipping regulations and industry best practices.',
      count: '5+'
    },
    {
      role: 'Support Staff',
      description: 'Dedicated support team ensuring smooth operations and excellent customer service.',
      count: '15+'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={about_us}
          alt="Maritime operations"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">About United Marine Agency LLP</h1>
              <p className="text-xl md:text-2xl text-blue-100">
                Your trusted partner in professional maritime consultancy and survey services since 2025
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-semibold text-orange mb-6">Who We Are</h2>
                <p className="text-lg text-gray-500 leading-relaxed">
                  United Marine Agency LLP was incorporated as a marine consultancy and survey company in 2025, with our head office strategically located in Mumbai, India.
                </p>
                <p className="text-lg text-gray-500 leading-relaxed">
                  Our company is headed by a team of professionals who possess vast experience in shipping, surveying, and marine consultancy. The team at United Marine Agency LLP is recognized both in India and overseas for its diligence, personal reputation, and professional experience.
                </p>
                <p className="text-lg text-gray-500 leading-relaxed">
                  We are committed to upholding the organization's standards and best practices, ensuring that our clients receive top-notch service and full compliance with international regulations.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={service}
                  alt="Container ship"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover"
                />
                <img
                  src={main2}
                  alt="Port operations"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Mission & Vision */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
              <h3 className="text-4xl font-semibold text-orange mb-6">Our Vision & Mission</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Our Vision */}
            <div
              className="group relative bg-white rounded-sm border border-gray-200
              p-6 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* Top Accent Line */}
              <span className="absolute top-0 left-0 h-1 w-0 bg-orange transition-all duration-300 group-hover:w-full rounded-t-2xl"></span>

              {/* Header */}
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="w-10 h-10 rounded-sm bg-orange/10 flex items-center justify-center shrink-0
                  transition-transform duration-300 group-hover:scale-105"
                >
                  <svg
                    className="w-7 h-7 text-orange"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M12 3c4.97 0 9 3.582 9 8 0 5.25-7.5 10-9 10s-9-4.75-9-10c0-4.418 4.03-8 9-8z"
                    />
                  </svg>
                </div>

                <h4 className="text-2xl font-semibold text-gray-900">
                  Our Vision
                </h4>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted name in maritime consulting, representing clients
                in high-profile shipping incidents globally. We aspire to set industry
                standards through unwavering commitment to excellence and professional
                integrity.
              </p>
            </div>

            {/* Our Mission */}
            <div
              className="group relative bg-white rounded-sm border border-gray-200
              p-6 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* Top Accent Line */}
              <span className="absolute top-0 left-0 h-1 w-0 bg-orange transition-all duration-300 group-hover:w-full rounded-t-2xl"></span>

              {/* Header */}
              <div className="flex items-center gap-4 mb-5">
                <div
                  className="w-10 h-10 rounded-sm bg-orange/10 flex items-center justify-center shrink-0
                  transition-transform duration-300 group-hover:scale-105"
                >
                  <svg
                    className="w-7 h-7 text-orange"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M9 5a2 2 0 002 2h2a2 2 0 002-2"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M9 14l2 2 4-4"
                    />
                  </svg>
                </div>

                <h4 className="text-2xl font-semibold text-gray-900">
                  Our Mission
                </h4>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed">
                To provide factual and flawless reporting to our valuable clientele
                through the peerless skills, expertise, and experience of our dedicated
                marine consultants and surveyors, ensuring complete client satisfaction.
              </p>
            </div>

          </div>
        </div>
      </motion.section>


      {/* Core Values */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-semibold text-orange mb-6">Our Core Values</h2>
              <p className="text-lg text-gray-500 leading-relaxed">The principles that guide everything we do</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="
                    bg-gray-50 p-6 border border-gray-200
                    hover:shadow-xl transition-all duration-300
                    hover:-translate-y-2
                  "
                >
                  {/* Icon + Title Row */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-orange">
                      {value.icon}
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800">
                      {value.title}
                    </h4>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-lg leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </motion.section>

      {/* Team Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-semibold text-orange mb-6">Our Professional Team</h2>
              <p className="text-lg text-gray-500 leading-relaxed">Experienced professionals dedicated to excellence</p>
            </div>

            <div className="grid md:grid-cols-3 gap-12 text-center">
              {team.map((member, index) => (
                <div key={index} className="flex flex-col items-center border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white p-8">
                  
                  {/* Count */}
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-orange">
                      {member.count}
                    </span>
                  </div>

                  {/* Role */}
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">
                    {member.role}
                  </h4>

                  {/* Description */}
                  <p className="text-gray-600 text-lg leading-relaxed max-w-sm">
                    {member.description}
                  </p>

                </div>
              ))}
            </div>

          </div>
        </div>
      </motion.section>

      {/* Why Choose Us */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-semibold text-orange mb-6">Why Choose Us</h2>
              <p className="text-lg text-gray-500 leading-relaxed">What sets us apart in the maritime industry</p>
            </div>

            <div className="grid md:grid-cols-2 gap-14">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Experienced Professionals</h4>
                  <p className="text-gray-600 text-lg">Our team brings decades of combined experience in maritime consultancy and surveying.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Global Recognition</h4>
                  <p className="text-gray-600 text-lg">Recognized both in India and overseas for our diligence and professional reputation.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Accurate Reporting</h4>
                  <p className="text-gray-600 text-lg">We provide factual and flawless reporting with meticulous attention to detail.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Compliance Assured</h4>
                  <p className="text-gray-600 text-lg">Full compliance with international regulations and industry best practices.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Quick Turnaround</h4>
                  <p className="text-gray-600 text-lg">Fast and efficient service delivery without compromising on quality.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange rounded-sm flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-600 mb-2">Client-Centric Approach</h4>
                  <p className="text-gray-600 text-lg">We tailor our services to meet each client's specific needs and requirements.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  );
};

export default AboutUs;